import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "@/redux/axios";

// Plan Types
export interface Addon {
  addon_id: string;
  name: string;
  description: string;
  price: number;
  type: string;
  features: string[];
}

export interface Plan {
  plan_id: string;
  name: string;
  price: number;
  duration_days: number;
  is_subscription: boolean;
  credits: number;
  description: string;
  features: string[];
  is_popular: boolean;
  razor_pay_plan_id: string | null;
  addons: Addon[];
  savings: string | null;
  adjusted_amount: number | null;
}

interface PricingState {
  plans: Plan[];
  isFetching: boolean;
  isError: boolean;
  errorMessage: string;
}



const initialState: PricingState = {
  plans: [],
  isFetching: false,
  isError: false,
  errorMessage: "",
};

// Fetch all plans
export const fetchPlans = createAsyncThunk(
  "pricing/fetchPlans",
  async (_, thunkAPI) => {
    try {
      console.log("Fetching plans...");
      const response = await axios.get("/plans");

      console.log("Plans response:", response.data);

      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching plans:", err);
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to fetch plans" }
      );
    }
  }
  
);

// Create Payment Order
export const createOrder = createAsyncThunk(
  "pricing/createOrder",
  async (planId: string, thunkAPI) => {
    try {
      const response = await axios.post("/api/one-time-payment/create-order", {
        plan_id: planId,
      });
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create order"
      );
    }
  }
);

// Verify Payment
export const verifyPayment = createAsyncThunk(
  "pricing/verifyPayment",
  async (data: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }, thunkAPI) => {
    try {
      const response = await axios.post("/api/one-time-payment/verify", data);
      return response.data; // Expecting { status: true, credits_added: number }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Payment verification failed"
      );
    }
  }
);

export const pricingSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {
    clearPlans: (state) => {
      state.plans = [];
      state.isError = false;
      state.errorMessage = "";
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Plans
      .addCase(fetchPlans.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.isFetching = false;
        state.plans = action.payload || [];
        state.isError = false;
      })
      .addCase(fetchPlans.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to fetch plans";
      })

      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(createOrder.fulfilled, (state) => {
        state.isFetching = false;
        state.isError = false;
      })
      .addCase(createOrder.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload || "Failed to create order";
      })

      // Verify Payment
      .addCase(verifyPayment.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(verifyPayment.fulfilled, (state) => {
        state.isFetching = false;
        state.isError = false;
      })
      .addCase(verifyPayment.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload || "Payment verification failed";
      });
  },
});

export const { clearPlans, clearError } = pricingSlice.actions;

export const pricingSelector = (state: any) => state.pricing;

export const pricingReducer = pricingSlice.reducer;
export default pricingSlice.reducer;
